import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Skeleton, SkuThumb } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, ChevronDown, ChevronRight, AlertTriangle, Search, ArrowUp, ArrowDown } from "lucide-react";

const SORT_LABELS = { units_sold: "units sold", returns: "returns", return_rate: "return rate" };

const SortTh = ({ label, k, sort, setSort }) => {
  const active = sort.key === k;
  return (
    <th
      className="text-right px-3 py-2.5 font-semibold cursor-pointer select-none hover:text-foreground transition-colors"
      onClick={() => setSort({ key: k, dir: active && sort.dir === "desc" ? "asc" : "desc" })}
      data-testid={`returns-sort-${k}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (sort.dir === "desc" ? <ArrowDown className="h-3 w-3" aria-hidden="true" /> : <ArrowUp className="h-3 w-3" aria-hidden="true" />)}
      </span>
    </th>
  );
};

const RangeToggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="returns-range-toggle">
    {[7, 30, 60, 90].map((d) => (
      <button
        key={d}
        onClick={() => onChange(d)}
        data-testid={`returns-range-${d}`}
        className={`px-3.5 py-1.5 text-xs font-semibold rounded-full transition-colors ${
          value === d ? "bg-blue-600 text-white" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        {d}d
      </button>
    ))}
  </div>
);

const rateTone = (rate) => {
  if (rate == null) return "text-muted-foreground";
  if (rate >= 15) return "text-red-600 dark:text-red-400 font-bold";
  if (rate >= 8) return "text-amber-600 dark:text-amber-400 font-semibold";
  if (rate >= 3) return "text-yellow-600 dark:text-yellow-400";
  return "text-emerald-600 dark:text-emerald-400";
};

const ReasonBar = ({ reasons, totalReturns }) => {
  if (!reasons?.length) return <span className="text-xs text-muted-foreground">—</span>;
  const top = reasons[0];
  const pct = totalReturns ? Math.round(top.qty / totalReturns * 100) : 0;
  return (
    <div className="min-w-0" data-testid="reason-cell">
      <p className="text-xs font-medium truncate" title={top.reason}>{top.reason}</p>
      <div className="flex items-center gap-1.5 mt-0.5">
        <div className="h-1 flex-1 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-amber-500" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-[10px] text-muted-foreground tabular-nums">{top.qty} · {pct}%</span>
      </div>
    </div>
  );
};

const SkuReasonsRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  const hasReturns = row.returns > 0;

  return (
    <>
      <tr
        className="border-t border-border hover:bg-muted/40 transition-colors cursor-pointer"
        onClick={() => hasReturns && setOpen(!open)}
        data-testid={`returns-row-${row.sku}`}
      >
        <td className="pl-3 py-2 w-8">
          {hasReturns && (open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />)}
        </td>
        <td className="py-2 w-14"><SkuThumb sku={row.sku} alt={row.title} /></td>
        <td className="px-3 py-2">
          <p className="font-medium max-w-[22rem] truncate" title={row.title}>{row.title || row.sku}</p>
          <p className="font-mono text-[11px] text-muted-foreground">{row.sku}{row.asin && ` · ${row.asin}`}</p>
        </td>
        <td className="px-3 py-2 text-right tabular-nums font-semibold">{row.units_sold.toLocaleString("en-IN")}</td>
        <td className="px-3 py-2 text-right tabular-nums">{row.returns.toLocaleString("en-IN")}</td>
        <td className={`px-3 py-2 text-right tabular-nums ${rateTone(row.return_rate)}`}>
          {row.return_rate != null ? `${row.return_rate}%` : "—"}
        </td>
        <td className="px-3 py-2 max-w-[16rem]"><ReasonBar reasons={row.top_reasons} totalReturns={row.returns} /></td>
      </tr>
      {open && hasReturns && (
        <tr className="border-t border-border/50 bg-muted/20" data-testid={`returns-detail-${row.sku}`}>
          <td colSpan={7} className="px-4 py-3">
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">Top reasons for this product</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {row.top_reasons.map((r, i) => {
                const pct = row.returns ? Math.round(r.qty / row.returns * 100) : 0;
                return (
                  <div key={i} className="flex items-center gap-2" data-testid={`reason-${row.sku}-${i}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" title={r.reason}>{r.reason}</p>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-1">
                        <div className="h-full bg-amber-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <span className="text-xs font-semibold tabular-nums w-16 text-right">{r.qty} · {pct}%</span>
                  </div>
                );
              })}
            </div>
            {row.last_return && (
              <p className="text-[11px] text-muted-foreground mt-3">Latest return: {row.last_return}</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

export const ReturnsAnalysis = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "units_sold", dir: "desc" });

  const load = () => api.get(`/tools/returns-analysis?days=${days}`).then(({ data }) => setData(data));
  useEffect(() => { setData(null); load(); }, [days]);

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post("/tools/returns-analysis/sync");
      toast.info("Refreshing returns from Amazon — this typically takes 1–3 minutes");
      setTimeout(() => { load(); setSyncing(false); }, 90000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
      setSyncing(false);
    }
  };

  if (!data) return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-64" /></div>;

  const overallRate = data.total_units ? (data.total_returns / data.total_units * 100).toFixed(2) : "0.00";
  const noData = !data.last_sync && data.rows.length === 0;
  const query = q.trim().toLowerCase();
  let rows = query
    ? data.rows.filter((r) =>
        (r.title || "").toLowerCase().includes(query) ||
        (r.sku || "").toLowerCase().includes(query) ||
        (r.asin || "").toLowerCase().includes(query))
    : data.rows;
  rows = [...rows].sort((a, b) => {
    const va = a[sort.key] ?? -1, vb = b[sort.key] ?? -1;
    return sort.dir === "desc" ? vb - va : va - vb;
  });

  return (
    <div className="space-y-6" data-testid="returns-analysis">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold">Returns Analysis</h2>
          <p className="text-xs text-muted-foreground">
            FBA customer returns with reason codes · sorted by best sellers
            {data.last_sync && ` · last sync ${new Date(data.last_sync).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RangeToggle value={days} onChange={setDays} />
          <Button size="sm" className="rounded-full" onClick={sync} disabled={syncing || data.running} data-testid="returns-sync-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${(syncing || data.running) ? "animate-spin" : ""}`} aria-hidden="true" />
            {syncing || data.running ? "Refreshing…" : "Refresh from Amazon"}
          </Button>
        </div>
      </div>

      {noData && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="returns-empty-hint">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold">No returns data yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Amazon&apos;s FBA customer returns report (last 90 days) hasn&apos;t been pulled yet. Hit &ldquo;Refresh from Amazon&rdquo; — it typically completes within 1–3 minutes and will start showing SKU-level reasons.
            </p>
          </div>
        </div>
      )}

      {data.error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-3 text-sm text-red-600 dark:text-red-400" data-testid="returns-error">
          {data.error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Returns" value={data.total_returns.toLocaleString("en-IN")} sub={`over ${days} days`} testId="kpi-returns" />
        <KpiCard label="Units sold" value={data.total_units.toLocaleString("en-IN")} sub={`over ${days} days`} testId="kpi-units-sold" />
        <KpiCard label="Return rate" value={`${overallRate}%`} sub="returns / units sold" testId="kpi-return-rate" toneClass={rateTone(parseFloat(overallRate))} />
        <KpiCard label="SKUs with returns" value={data.skus_with_returns.toLocaleString("en-IN")} sub={`of ${data.rows.length} sellers`} testId="kpi-skus-with-returns" />
      </div>

      {data.reasons_summary?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4 shadow-sm" data-testid="reasons-summary">
          <h3 className="text-sm font-semibold mb-3">Top return reasons ({days} days)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            {data.reasons_summary.map((r, i) => {
              const pct = data.total_returns ? Math.round(r.qty / data.total_returns * 100) : 0;
              return (
                <div key={i} className="flex items-center gap-3" data-testid={`summary-reason-${i}`}>
                  <span className="text-xs font-mono text-muted-foreground w-5 text-right">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm font-medium truncate" title={r.reason}>{r.reason}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">{r.qty} · {pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-1">
                      <div className="h-full bg-amber-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-full max-w-xs">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search product, SKU or ASIN…"
            className="pl-9 h-9 rounded-full"
            data-testid="returns-search-input"
          />
        </div>
        {query && (
          <span className="text-xs text-muted-foreground" data-testid="returns-search-count">
            {rows.length} match{rows.length === 1 ? "" : "es"}
          </span>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="returns-table">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2.5"></th>
              <th className="px-3 py-2.5"></th>
              <th className="text-left px-3 py-2.5 font-semibold">Product</th>
              <SortTh label="Units sold" k="units_sold" sort={sort} setSort={setSort} />
              <SortTh label="Returns" k="returns" sort={sort} setSort={setSort} />
              <SortTh label="Rate" k="return_rate" sort={sort} setSort={setSort} />
              <th className="text-left px-3 py-2.5 font-semibold">Top reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 200).map((r) => <SkuReasonsRow key={r.sku} row={r} />)}
          </tbody>
        </table>
        {rows.length === 0 && !noData && (
          <p className="text-sm text-muted-foreground p-6 text-center" data-testid="returns-no-match">
            {query ? "No products match your search." : "No sales or returns in this range."}
          </p>
        )}
        {rows.length > 200 && (
          <p className="text-[11px] text-muted-foreground text-center py-2">
            Showing top 200 of {rows.length} products (sorted by {SORT_LABELS[sort.key]}, {sort.dir === "desc" ? "highest first" : "lowest first"})
          </p>
        )}
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, sub, testId, toneClass }) => (
  <div className="bg-card border border-border rounded-lg p-5 shadow-sm" data-testid={testId}>
    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</p>
    <p className={`font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-2 ${toneClass || ""}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1.5">{sub}</p>}
  </div>
);
